import {
  IonContent,
  IonPage,
  useIonViewDidEnter,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from '@ionic/react';
import React, { useState } from 'react';
import './Default.css';
import AppPageHeader from '../components/AppPageHeader';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router';
import useEnhancedAuth from '../EnhancedAuth';
import {
  apiCardAutoload,
  apiCardAutoloadRemove,
  apiCardAutoloads,
  apiCardAutoloadUpdate,
  apiGetPaymentMethods,
} from '../services/api';
import CardAutoloadNone from '../usecases/cardautoload/CardAutoloadNone';
import { FarecardAutoload, PaymentMethod } from '../services/types/apitypes';
import CardAutoloadEdit from '../usecases/cardautoload/CardAutoloadEdit';
import {
  getAppError,
  getAppSuccess,
  getUserMessageOrValidationError,
} from '../utils/messages';
import PaymentAddProcess from '../usecases/shared/PaymentAddProcess';
import PaymentsList, {AdditionalPaymentMethod} from '../usecases/payments/PaymentsList';
import { pickInitialPaymentMethod } from '../usecases/payments/preferredPayment';
import AppSeparator from '../components/AppSeparator';
import PaymentAddButton from '../usecases/payments/PaymentAddButton';
import CardAutoloadPay from '../usecases/cardautoload/CardAutoloadPay';
import CardAutoloadDetails from '../usecases/cardautoload/CardAutoloadDetails';
import { LoadingController } from '../utils/LoadingController';
import { useToaster } from '../hooks/useToaster';

const CardAutoloadPage: React.FC = () => {
  const auth = useEnhancedAuth();
  const [t] = useTranslation('global');
  const router = useHistory();
  const [present] = useToaster();

  const { cardid } = useParams<{ cardid: string }>();

  const [block, setBlock] = useState<string>('start');

  const minimumValue = 3;
  const maximumValue = 500;
  const DEFAULT_VALUE = 3;

  const [selectedValue, setSelectedValue] = useState<number>(DEFAULT_VALUE);
  const [selectedThreshold, setSelectedThreshold] = useState<number>(5);
  const [autoload, setAutoload] = useState<FarecardAutoload | undefined>(
    undefined,
  );

  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    PaymentMethod | undefined
  >(undefined);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [isPageActive, setIsPageActive] = useState(false);
  useIonViewDidEnter(() => {
    setIsPageActive(true);
  });
  useIonViewWillLeave(() => {
    setIsPageActive(false);
  });

  const getAutoload1ValidationError = () => {
    if (selectedValue < minimumValue) {
      return t('cardvalue.errorminvalue', { v: minimumValue });
    }
    const currentCardValue = selectedThreshold;
    if (selectedValue + currentCardValue > maximumValue) {
      return t('cardautoload.errormaxvalue', { v: maximumValue });
    }
    return false;
  };

  const getAutoload2ValidationError = () => {
    const err1 = getAutoload1ValidationError();
    if (err1 !== false) {
      return err1;
    }
    if (!selectedPaymentMethod) {
      return t('cardautoload.errorpayment');
    }
    return false;
  };

  // dont use useEffect, it does not trigger the second time a page with the same parameter is loaded.
  // we want the page to reload the current data every time it is opened
  useIonViewWillEnter(() => {
    setSelectedValue(DEFAULT_VALUE);
    loadAutoload().then(() => console.log('Loaded history'));
    loadPayments().then(() => console.log('Loaded history'));
  }, [auth.isLoading]);

  const loadAutoload = async () => {
    try {
      LoadingController.show();
      const result = await apiCardAutoloads(cardid, auth);
      if (result.data && result.data[0]) {
        const al: FarecardAutoload = result.data[0];
        setSelectedValue(al.autoloadAmount / 100);
        setSelectedThreshold(al.balanceThreshold / 100);
        setAutoload(al);
      } else {
        setSelectedValue(DEFAULT_VALUE);
        setAutoload(undefined);
      }
    } catch (error) {
      console.log('Error loading history', error);
    } finally {
      LoadingController.hide();
    }
  };

  const goToEdit = () => {
    setBlock('edit');
  };

  const loadPayments = async () => {
    LoadingController.show();
    try {
      const response = await apiGetPaymentMethods(auth);
      setPaymentMethods(response.data);
      const initial = pickInitialPaymentMethod(response.data, []);
      if (initial && typeof initial !== 'string') {
        setSelectedPaymentMethod(initial);
      }
    } catch (error) {
      console.log('Could not load payments.');
    } finally {
      LoadingController.hide();
    }
  };

  const goToEditPay = async () => {
    const validationError = getAutoload1ValidationError();
    if (validationError !== false) {
      present(getAppError(validationError));
      return;
    }
    setBlock('editpay');
  };

  const selectPaymentMethod = (p: PaymentMethod | AdditionalPaymentMethod) => {
    if (typeof p === "string") return;
    setSelectedPaymentMethod(p);
    setBlock('editpay');
  };

  const chosePayments = () => {
    setBlock('selectpayment');
  };

  const addPaymentMethod = () => {
    setBlock('addpayment');
  };

  const returnFromAddPayment = async () => {
    await loadPayments().then(() => console.log('Loaded payments'));
    setBlock('editpay');
  };

  const setAutoloadFinish = async (
    value: number,
    threshold: number,
    dayOfMonth: number,
    paymentMethod: PaymentMethod,
  ) => {
    const validationError = getAutoload2ValidationError();
    if (validationError !== false) {
      present(getAppError(validationError));
      return;
    }

    if (dayOfMonth <= 0) {

      await saveAutoload(
          {
            cardId: cardid,
            paused: false,
            balanceThreshold: threshold * 100,
            autoloadAmount: value * 100,
            primaryPaymentMethod: {
              id: paymentMethod.id,
            },
          },
          autoload?.autoloadId || undefined,
      );
    } else {
      await saveAutoload(
          {
            cardId: cardid,
            paused: false,
            dayOfMonthThreshold: dayOfMonth,
            autoloadAmount: value * 100,
            primaryPaymentMethod: {
              id: paymentMethod.id,
            },
          },
          autoload?.autoloadId || undefined,
      );
    }
  };

  const saveAutoload = async (data: any, id?: number) => {
    if (isSubmitting) {
      return;
    }

    try {
      LoadingController.show();
      setIsSubmitting(true);

      if (id) {
        await apiCardAutoloadUpdate(cardid, id, data, auth);
      } else {
        await apiCardAutoload(cardid, data, auth);
      }
      await loadAutoload();
      setBlock('start');
      present(getAppSuccess(t('cardautoload.success')));
    } catch (error) {
      console.error('Error setting autoload ', error);
      const apiError = getUserMessageOrValidationError(error);
      if (typeof apiError === 'string') {
        present(getAppError(apiError));
      } else {
        present(getAppError(t('cardautoload.nosuccess')));
      }
    } finally {
      setIsSubmitting(false);
      LoadingController.hide();
    }
  };

  const toggleAutoload = async (al: FarecardAutoload) => {
    await saveAutoload(
      {
        cardId: cardid,
        paused: !al.paused,
        balanceThreshold: al.balanceThreshold,
        autoloadAmount: al.autoloadAmount,
        fundingSourceId: al.fundingSourceId,
        primaryPaymentMethod: {
          id: getPaymentMethodId(al),
        },
      },
      al.autoloadId,
    );
  };

  const getPaymentMethodId = (al: FarecardAutoload) => {
    return (
      al.primaryPaymentMethod?.id ||
      al.multiplePaymentMethods[0].primaryPaymentMethodId
    );
  };

  const goBack = () => {
    router.push('/card/' + cardid + '/options');
  };

  const getPaymentInfo = () => {
    if (paymentMethods && paymentMethods.length) {
      if (autoload) {
        const currentId = getPaymentMethodId(autoload);
        for (const paymentMethod of paymentMethods) {
          if (paymentMethod.id == currentId) {
            return paymentMethod.creditCardType + ' ' + paymentMethod.maskedPan;
          }
        }
      }
    }
    return '';
  };

  const removeAutoload = async (al: FarecardAutoload) => {
    try {
      LoadingController.show();
      setIsSubmitting(true);

      await apiCardAutoloadRemove(cardid, al.autoloadId, auth);
      await loadAutoload();
      setBlock('start');
      present(getAppSuccess(t('cardautoload.removesuccess')));
    } catch (error) {
      console.error('Error deleting autoload ', error);
      const apiError = getUserMessageOrValidationError(error);
      if (typeof apiError === 'string') {
        present(getAppError(apiError));
      } else {
        present(getAppError(t('cardautoload.removenosuccess')));
      }
    } finally {
      setIsSubmitting(false);
      LoadingController.hide();
    }
  };

  return (
    <IonPage id="farecardautoload-page">
      {block === 'addpayment' && (
        <PaymentAddProcess
          isPageActive={isPageActive}
          callback={returnFromAddPayment}
        />
      )}
      {block === 'selectpayment' && (
        <>
          <AppPageHeader
            isPageActive={isPageActive}
            back={() => setBlock('editpay')}
            title={t('cardautoload.title')}
          ></AppPageHeader>
          <IonContent fullscreen className="ion-padding default-content">
            <PaymentsList
              payments={paymentMethods}
              selectPayment={selectPaymentMethod}
              additionalPayments={[]}
            />
            <AppSeparator />
            <PaymentAddButton click={addPaymentMethod} />
          </IonContent>
        </>
      )}
      {block === 'start' && (
        <>
          <AppPageHeader
            isPageActive={isPageActive}
            back={() => goBack()}
            title={t('cardautoload.title')}
          ></AppPageHeader>
          <IonContent fullscreen className="ion-padding default-content">
            {autoload ? (
              <CardAutoloadDetails
                paymentinfo={getPaymentInfo()}
                autoload={autoload}
                toggle={toggleAutoload}
                remove={removeAutoload}
                edit={() => setBlock('edit')}
              />
            ) : (
              <CardAutoloadNone next={goToEdit} />
            )}
          </IonContent>
        </>
      )}
      {block === 'edit' && (
        <>
          <AppPageHeader
            isPageActive={isPageActive}
            back={() => setBlock('start')}
            title={t('cardautoload.title')}
          ></AppPageHeader>
          <IonContent fullscreen className="ion-padding default-content">
            <CardAutoloadEdit
              isValid={getAutoload1ValidationError() === false}
              minimumValue={minimumValue}
              maximumValue={maximumValue}
              currentCardValue={selectedThreshold}
              selectedValue={selectedValue}
              setSelectedValue={setSelectedValue}
              next={goToEditPay}
            />
          </IonContent>
        </>
      )}
      {block === 'editpay' && (
        <>
          <AppPageHeader
            isPageActive={isPageActive}
            back={() => setBlock('edit')}
            title={t('cardautoload.title')}
          ></AppPageHeader>
          <IonContent fullscreen className="ion-padding default-content">
            <CardAutoloadPay
              isValid={getAutoload2ValidationError() === false}
              selectedThreshold={selectedThreshold}
              chosePayment={chosePayments}
              addPaymentMethod={addPaymentMethod}
              paymentMethod={selectedPaymentMethod}
              setSelectedThreshold={setSelectedThreshold}
              next={(p: PaymentMethod, dayOfMonth: number) =>
                setAutoloadFinish(selectedValue, selectedThreshold, dayOfMonth, p)
              }
            />
          </IonContent>
        </>
      )}
    </IonPage>
  );
};

export default CardAutoloadPage;
